import { Game, GameTypes, LiveData } from '../../apiTypes/GameService';
import type { TileProps } from './tiles';
import { formatNumber, formatNumberToUnit, GAME_TITLE_MISSING, getCasinoPathname } from '../../helpers/tileHelper';
import { CategoryLiveData } from '../../hooks/useCategoryLiveData';
import { toUrlFriendlyName } from '../../helpers/stringConverter';
import { getUrlWithoutParameters } from '@SharedScripts/Utils/Url';

interface GameDataAdapter {
  elementId: string | null;
  gameData: Game;
  liveData?: LiveData;
  categoryLiveData?: CategoryLiveData;
  favorited?: boolean;
  isLoading: boolean;
  index?: number;
  subtitle?: string;
  primaryButtonCallback?: () => void;
  buttonEnabled?: boolean;
  primaryButtonText?: string | null;
  secondaryButtonHref?: string;
  primaryButtonHref?: string;
  secondaryButtonText?: string | null;
}

export const gameDataAdapter = ({
  gameData,
  liveData,
  categoryLiveData,
  favorited = false,
  isLoading,
  index,
  subtitle,
  primaryButtonHref,
  primaryButtonCallback,
  buttonEnabled,
  primaryButtonText,
  secondaryButtonHref,
  secondaryButtonText
}: GameDataAdapter) => {
  const { releaseDate, popscore, type, title, description, tagLine, minWager, gameNotAvailable, graphics, id, metaData, stream } =
    gameData ?? {};
  const { machineData, tableData } = liveData ?? {};
  const { amountOfDanishDealers, amountOfLiveGames, categoryLiveStreamLink } = categoryLiveData ?? {};
  const { jackpot } = machineData ?? {};
  const {
    totalSeats,
    playerSeated,
    players,
    dealer,
    openingHours,
    open,
    imageUrls,
  } = tableData ?? {};
  const { openFrom } = openingHours ?? {};

  // Todo: Consider which images should be passed on to the compponents
  const background = imageUrls?.imageUrlExtraLarge ?? graphics?.background?.url ?? '';
  const foreground = imageUrls?.imageUrlExtraLarge ? undefined : (graphics?.foreground?.url || '');
  const portrait = (graphics?.portrait?.url || '');
  const logo = (graphics?.logo?.url || '');
  const landscape = (graphics?.landscape?.url || '');
  const thumbnail = (graphics?.thumbnail?.url || '');

  const minWagerText = minWager || minWager === 0 ? `Min. ${formatNumberToUnit(minWager, true)}` : undefined;
  const potSizeText = jackpot || jackpot === 0 ? formatNumberToUnit(Math.floor(jackpot)) : undefined;

  const liveGamesText = amountOfLiveGames ? `${amountOfLiveGames} Live spil` : undefined;
  const dealerName = amountOfDanishDealers
    ? `${amountOfDanishDealers} ${amountOfDanishDealers === 1 ? 'Dansk dealer' : 'Danske dealere'}`
    : dealer?.nationality === 'DK' ? dealer?.name ?? '' : undefined;
  const playersInfoText = totalSeats
    ? `${formatNumber(playerSeated?.length)}/${formatNumber(totalSeats)}`
    : formatNumber(players);


  const titleForUrl = toUrlFriendlyName(title || '');
  const isGameUnavailable = gameNotAvailable;
  const pathName = getCasinoPathname();

  return {
    navigationUrl: isGameUnavailable || title?.toLowerCase().replaceAll(' ', '').includes('superfavorit') ? undefined : (primaryButtonHref ? primaryButtonHref : `/${pathName}/spil/${titleForUrl}?returnUrl=${encodeURI(getUrlWithoutParameters(location.href))}`),
    navigationUrlWithLaunchParam: isGameUnavailable || title?.toLowerCase().replaceAll(' ', '').includes('superfavorit') ? undefined : (primaryButtonHref ? primaryButtonHref : `/${pathName}/spil/${titleForUrl}?launch&fullscreen&returnUrl=${encodeURI(getUrlWithoutParameters(location.href))}`),

    // Get data from games API endpoint
    id: id ?? undefined,
    type: type ?? GameTypes.CASINO,
    title: title ?? GAME_TITLE_MISSING,
    description: description?.full ?? undefined,
    subtitle,
    tag: tagLine ?? undefined,
    minWager: minWagerText,
    minWagerRaw: minWager || 0,
    isUnavailable: gameNotAvailable,
    releaseDate: new Date(releaseDate ?? ''),
    popscore,
    bannerInfo: metaData?.banner ?? undefined,
    gameGraphics: {
      backgroundUrl: background,
      foregroundUrl: foreground,
      portraitUrl: portrait,
      thumbnailUrl: thumbnail,
      logoUrl: logo,
      landscapeUrl: landscape,
      assets: graphics?.assets || [],
      disabled: gameNotAvailable,
    },

    // Get data from LiveData API endpoint
    potSize: potSizeText,
    playersText: playersInfoText,
    dealerName,
    liveGamesText,
    liveStreamLink: (categoryLiveStreamLink || stream?.videoStreamUrl) ?? '',
    isTableClosed: !open && type === 'Live',
    openFrom: openFrom,

    // Get data from favorites API endpoint
    favourited: favorited,
    primaryButtonCallback: primaryButtonCallback,

    // Get data from the fetching state
    isLoading,
    index: typeof index === 'number' ? index + 1 : undefined,
    primaryButtonText: primaryButtonText ?? '',

    secondaryButtonHref,
    primaryButtonHref,
    secondaryButtonText: secondaryButtonText ?? '',
    buttonEnabled
  } satisfies TileProps;
};
